import React, { useEffect, useState } from "react";
import { ButtonBase, styled } from "@mui/material";

interface ISwitchButtonProps {
    choiceOne: string;
    choiceTwo: string;
    value?: boolean;
    onChange?: (value: boolean) => void;
    disabled?: boolean;
    className?: string;
}

const Track = styled(ButtonBase, {
    shouldForwardProp: (prop) => prop !== "checked",
})<{ checked: boolean }>(({ theme, checked }) => ({
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: 1,
    backgroundColor: checked ? theme.palette.primary.main : theme.palette.grey[400],
    transition: theme.transitions.create("background-color"),
    "&.Mui-disabled": {
        opacity: 0.5,
    },
}));

const Sizer = styled("span")({
    visibility: "hidden",
    display: "grid",
    gridTemplateColumns: "1fr 0.5fr",
});

const SizerCell = styled("span")({
    display: "block",
    padding: "1px 8px",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    textAlign: "center",
});

const Thumb = styled("span", {
    shouldForwardProp: (prop) => prop !== "checked",
})<{ checked: boolean }>(({ theme, checked }) => ({
    position: "absolute",
    top: 1,
    bottom: 1,
    left: checked ? "33.3334%" : 1,
    right: checked ? 1 : "33.3334%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    backgroundColor: theme.palette.background.paper,
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    transition: theme.transitions.create(["left", "right"]),
    pointerEvents: "none",
}));

const SwitchButton: React.FC<ISwitchButtonProps> = (props: ISwitchButtonProps) => {
    const [checked, setChecked] = useState<boolean>(!!props.value);

    useEffect(() => {
        setChecked(!!props.value);
    }, [props.value]);

    const handleToggle = () => {
        if (props.disabled) {
            return;
        }
        const nextValue = !checked;
        setChecked(nextValue);
        props.onChange?.(nextValue);
    };

    return (
        <Track
            className={props.className ?? ""}
            role="switch"
            aria-checked={checked}
            checked={checked}
            disabled={props.disabled}
            onClick={handleToggle}
        >
            <Sizer aria-hidden="true">
                <span>
                    <SizerCell>{props.choiceOne}</SizerCell>
                    <SizerCell>{props.choiceTwo}</SizerCell>
                </span>
                <span />
            </Sizer>
            <Thumb checked={checked}>
                {checked ? props.choiceTwo : props.choiceOne}
            </Thumb>
        </Track>
    );
}

export default SwitchButton;
